import { useEffect, useState } from "react";
import { useTranslation } from 'react-i18next';
import { Button, Card, Grid } from "@arco-design/web-react";
import { useSelector } from "react-redux";
import axios from "axios";

import { BookingForm } from "../BookingForm/BookingForm";
import DeleteMeetingModal from './DeleteMeetingModal';
import { UserAvatar } from "../UserAvatar/UserAvatar";
import { AdminMeeting } from "../../models/Meeting";
import { useBreakpoint } from "../../utils/hooks/useBreakpoint";
import { RootState } from "../../store/store";
import { endpoints } from "../../api/endpoints";

import './MeetingCard.css';

function splitDisplayName(fullName: string | undefined) {
  const parts = fullName?.trim().split(/\s+/).filter(Boolean) ?? [];
  return {
    firstName: parts[0],
    lastName: parts.slice(1).join(" ") || undefined,
  };
}

function useParticipantProfile(userId: number, fallbackName: string | undefined) {
  const fallbackParts = splitDisplayName(fallbackName);
  const [firstName, setFirstName] = useState<string | undefined>(fallbackParts.firstName);
  const [lastName, setLastName] = useState<string | undefined>(fallbackParts.lastName);

  useEffect(() => {
    const parts = splitDisplayName(fallbackName);
    setFirstName(parts.firstName);
    setLastName(parts.lastName);
  }, [fallbackName]);

  useEffect(() => {
    if (!userId) {
      return;
    }

    axios
      .get(`${endpoints.getPublicProfile}${userId}`)
      .then((response) => {
        if (response.data.name) {
          setFirstName(response.data.name);
        }
        if (response.data.last_name) {
          setLastName(response.data.last_name);
        }
      })
      .catch((error) => {
        console.error("Error fetching meeting participant profile:", error);
      });
  }, [userId]);

  return { firstName, lastName };
}

type Props = {
  meeting: AdminMeeting;
  onUpdateMeeting: (date: string, time: number) => void;
  onDeleteMeeting: () => void;
}

const Row = Grid.Row;
const Col = Grid.Col;

export function MeetingCard({ meeting, onUpdateMeeting, onDeleteMeeting }: Props) {
  const { t } = useTranslation(); 
  const isCalendlySyncAvailable = useSelector((state: RootState) => state.config.isCalendlyLinkRequired);

  const weekDay = new Date(meeting.start_time).toLocaleDateString('en-US', { weekday: 'long' });
  const date = new Date(meeting.start_time).toLocaleDateString('en-US', {
    month: 'numeric',
    day: 'numeric',
    year: 'numeric'
  });
  const time = new Date(meeting.start_time).toLocaleTimeString('en-US', { hour: 'numeric', minute: 'numeric' });

  const { isMobile } = useBreakpoint('mobile');

  const [isEditMode, setIsEditMode] = useState(false);
  const [updatedDay, setUpdatedDay] = useState(meeting.date);
  const [updatedTime, setUpdatedTime] = useState(meeting.time);

  const [isSaveButtonDisabled, setSaveButtonDisabled] = useState(true);
  const handleButtonDisabled = (value: boolean) => {
    setSaveButtonDisabled(value);
  };

  useEffect(() => {
    if(updatedDay === meeting.date || updatedTime === meeting.time) {
      handleButtonDisabled(true);
    }

    if (updatedDay !== meeting.date || updatedTime !== meeting.time) {
      handleButtonDisabled(false);
    }
  }, [meeting.date, meeting.time, updatedDay, updatedTime]);

  const mentorNameParts = useParticipantProfile(meeting.mentor_user_id, meeting.mentor_name);
  const menteeNameParts = useParticipantProfile(meeting.mentee_user_id, meeting.mentee_name);

  return (
    <Card bordered={false} className="meeting-card">
      <Row align="center">
        <Col span={8}>
          <div className="meeting-card__name">{meeting.mentor_name}</div>
          <UserAvatar
            avatarUrl={meeting.mentor_avatar_url}
            firstName={mentorNameParts.firstName}
            lastName={mentorNameParts.lastName}
            size={64}
            shape="circle"
            alt={meeting.mentor_name}
            className="user-card__avatar"
          />
        </Col>
        <Col span={8}>
          {!isEditMode && (
            <div className="meeting-card__date-wrapper">
              <div className="meeting-card__week-day">{weekDay}</div>
              <div className="meeting-card__date">{date}</div>
              <div className="meeting-card__time">{time}</div>
            </div>
          )}
        </Col>
        <Col span={8}>
          <div className="meeting-card__name">{meeting.mentee_name}</div>
          <UserAvatar
            avatarUrl={meeting.mentee_avatar_url}
            firstName={menteeNameParts.firstName}
            lastName={menteeNameParts.lastName}
            size={64}
            shape="circle"
            alt={meeting.mentee_name}
            className="user-card__avatar"
          />
        </Col>
      </Row>
      {!isEditMode && meeting.meeting_url && (
        <div className="meeting-card__join-link">
          <a href={meeting.meeting_url} target="_blank" rel="noopener noreferrer">
            {t('home_page.join_meeting')}
          </a>
        </div>
      )}
      {isEditMode && (
        <BookingForm
          day={updatedDay}
          time={updatedTime}
          onDayChange={setUpdatedDay}
          onTimeChange={setUpdatedTime}
          userId={meeting.mentor_user_id.toString()}
          includeSelectedSlotInList
        />
      )}
      {isEditMode ? (
        <div className="meeting-card__buttons">
          <Button
            className="meeting-card__button meeting-card__button-primary"
            shape={"round"}
            size={isMobile ? 'small' : 'large'}
            onClick={() => {
              onUpdateMeeting(updatedDay, updatedTime);
              setIsEditMode(false);
            }}
            disabled={isSaveButtonDisabled}
          >
            {t('common.save_button')}
          </Button>
          <Button
            className="meeting-card__button meeting-card__button-secondary"
            shape={"round"}
            size={isMobile ? 'small' : 'large'}
            onClick={() => {
              setIsEditMode(false);
              setUpdatedDay(meeting.date);
              setUpdatedTime(meeting.time);
            }}
          >
            {t('common.cancel_button')}
          </Button>
        </div>
        ) : (
        <div className="meeting-card__buttons">
          {!isCalendlySyncAvailable && <Button
            className="meeting-card__button meeting-card__button-primary"
            shape={"round"}
            size={isMobile ? 'small' : 'large'}
            onClick={() => setIsEditMode(true)}
          >
            {t('common.edit_button')}
          </Button>}
          <DeleteMeetingModal onDeleteMeeting={onDeleteMeeting}/>
        </div>
      )}
    </Card>
  )
}